import type { GoalHealthStatus, GoalSummary, GoalType } from '@/types';
import { token } from '@/lib/theme/tokens';
import {
  TargetIcon,
  PiggyBankIcon,
  HouseIcon,
  AirplaneIcon,
  LaptopIcon,
  ShieldIcon,
  GraduationCapIcon,
  CarIcon,
  HeartIcon,
  CreditCardIcon,
  TrendUpIcon,
} from '@phosphor-icons/react';

export const ICON_MAP: Record<string, React.ElementType> = {
  emergency: ShieldIcon,
  shield: ShieldIcon,
  home: HouseIcon,
  plane: AirplaneIcon,
  laptop: LaptopIcon,
  graduation: GraduationCapIcon,
  car: CarIcon,
  heart: HeartIcon,
  piggy: PiggyBankIcon,
  target: TargetIcon,
  credit: CreditCardIcon,
  trending: TrendUpIcon,
};

export function getGoalIcon(icon: string | null, type?: GoalType): React.ElementType {
  if (icon && ICON_MAP[icon]) return ICON_MAP[icon];
  if (type === 'Reserve') return ShieldIcon;
  if (type === 'DebtPayoff') return CreditCardIcon;
  return TargetIcon;
}

export function pct(n: number, d: number): number {
  if (!d) return 0;
  return Math.min(999, Math.round((n / d) * 100));
}

export function getHealthBadge(status: GoalHealthStatus) {
  switch (status) {
    case 'Saudavel':
      return { label: 'Saudável', bg: 'bg-success-surface', text: 'text-success-text' };
    case 'Atencao':
      return { label: 'Atenção', bg: 'bg-warning-surface', text: 'text-warning-text' };
    case 'Critica':
      return { label: 'Crítica', bg: 'bg-error-surface', text: 'text-error-text' };
    case 'Atrasada':
      return { label: 'Atrasada', bg: 'bg-error-surface', text: 'text-error-text' };
    case 'Concluida':
      return { label: 'Concluída', bg: 'bg-success-surface', text: 'text-success-text' };
    default:
      return { label: 'Indefinida', bg: 'bg-surface-sunken', text: 'text-text-secondary' };
  }
}

export function getProgressColor(status: GoalHealthStatus): string {
  switch (status) {
    case 'Saudavel':
    case 'Concluida':
      return 'bg-success';
    case 'Atencao':
      return 'bg-warning';
    case 'Critica':
    case 'Atrasada':
      return 'bg-error';
    default:
      return 'bg-neutral-400';
  }
}

export function getStatusHexColor(status: GoalHealthStatus): string {
  switch (status) {
    case 'Saudavel':
    case 'Concluida':
      return token('--success', '#059669');
    case 'Atencao':
      return token('--warning', '#f59e0b');
    case 'Critica':
    case 'Atrasada':
      return token('--error', '#ef4444');
    default:
      return token('--text-tertiary', '#94a3b8');
  }
}

export function formatTargetDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatMonthYear(date: Date): string {
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  return `${months[date.getMonth()]}/${String(date.getFullYear()).slice(2)}`;
}

export function formatYmShort(ym: string): string {
  // ym e.g. "2026-07" or full ISO
  const months = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const parts = ym.split('-');
  const y = parts[0];
  const m = parts[1];
  return `${months[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

export function monthsUntilDeadline(targetDate: string | null, today: Date = new Date()): number | null {
  if (!targetDate) return null;
  const target = new Date(targetDate);
  return (target.getFullYear() - today.getFullYear()) * 12 + (target.getMonth() - today.getMonth());
}

export function projectedMonthsToTarget(goal: GoalSummary): number {
  const remaining = goal.targetAmount - goal.currentAmount;
  if (remaining <= 0) return 0;
  if (!goal.monthlyContribution || goal.monthlyContribution <= 0) return Infinity;
  return Math.ceil(remaining / goal.monthlyContribution);
}

export type GoalRuntimeStatus = 'complete' | 'ok' | 'tight' | 'behind' | 'stalled' | 'untracked';

/**
 * Categorical palette for distinguishing goals (not statuses). Resolves to the
 * design-system chart hues (`--chart-1…8`) at runtime; the original colour-blind-safe
 * hexes are kept as per-slot fallbacks.
 */
export const GOAL_PALETTE: readonly string[] = [
  '#059669', // emerald-600
  '#0284c7', // sky-600
  '#7c3aed', // violet-600
  '#d97706', // amber-600
  '#db2777', // pink-600
  '#0d9488', // teal-600
  '#4f46e5', // indigo-600
  '#c026d3', // fuchsia-600
];

export function getGoalPaletteColor(index: number): string {
  const i = ((index % GOAL_PALETTE.length) + GOAL_PALETTE.length) % GOAL_PALETTE.length;
  return token(`--chart-${i + 1}`, GOAL_PALETTE[i]);
}

export function goalStatus(goal: GoalSummary): GoalRuntimeStatus {
  if (goal.type === 'Reserve') {
    const ratio = goal.targetAmount > 0 ? goal.currentAmount / goal.targetAmount : 0;
    return ratio >= 1 ? 'complete' : ratio >= 0.5 ? 'ok' : 'behind';
  }
  if (goal.currentAmount >= goal.targetAmount) return 'complete';
  // No deadline and no contribution = not enough data to judge health.
  if (!goal.targetDate && (!goal.monthlyContribution || goal.monthlyContribution <= 0)) {
    return 'untracked';
  }
  if (!goal.targetDate) return 'ok';
  const left = monthsUntilDeadline(goal.targetDate);
  const need = projectedMonthsToTarget(goal);
  if (need === Infinity) return 'stalled';
  if (left === null) return 'ok';
  if (need <= left) return 'ok';
  if (need <= left + 2) return 'tight';
  return 'behind';
}

export function runtimeStatusColor(status: GoalRuntimeStatus): string {
  switch (status) {
    case 'complete':
    case 'ok':
      return token('--success', '#059669');
    case 'tight':
      return token('--warning', '#f59e0b');
    case 'behind':
    case 'stalled':
      return token('--error', '#ef4444');
    case 'untracked':
      return token('--text-tertiary', '#94a3b8');
  }
}

export function runtimeStatusLabel(status: GoalRuntimeStatus): string {
  switch (status) {
    case 'complete':
      return 'concluída';
    case 'ok':
      return 'no ritmo';
    case 'tight':
      return 'ritmo apertado';
    case 'behind':
      return 'atrasada';
    case 'stalled':
      return 'parada';
    case 'untracked':
      return 'sem dados';
  }
}

export function addMonthsToToday(n: number, today: Date = new Date()): Date {
  return new Date(today.getFullYear(), today.getMonth() + n, 1);
}

export function simulateGoal(
  currentAmount: number,
  targetAmount: number,
  monthlyContribution: number,
  extraContribution: number,
) {
  const remaining = targetAmount - currentAmount;
  if (remaining <= 0) return { months: 0, savedMonths: 0 };
  const totalMonthly = monthlyContribution + extraContribution;
  if (totalMonthly <= 0) return { months: Infinity, savedMonths: 0 };
  const monthsWithExtra = Math.ceil(remaining / totalMonthly);
  const monthsWithout = monthlyContribution > 0 ? Math.ceil(remaining / monthlyContribution) : Infinity;
  const savedMonths = monthsWithout === Infinity ? 0 : monthsWithout - monthsWithExtra;
  return { months: monthsWithExtra, savedMonths };
}
